import { z } from 'zod';
import { isoDateSchema } from './common.js';

export const weatherSchema = z.enum([
  'clear',
  'cloudy',
  'rain',
  'storm',
  'snow',
  'fog',
  'wind',
  'hot',
  'cold',
]);
export type Weather = z.infer<typeof weatherSchema>;

export const rdoStatusSchema = z.enum(['draft', 'signed', 'corrected']);
export type RdoStatus = z.infer<typeof rdoStatusSchema>;

export const dailyLogSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  logDate: isoDateSchema,
  weather: weatherSchema.nullable(),
  temperatureC: z.number().int().min(-60).max(60).nullable(),
  crewSize: z.number().int().min(0).max(1000).nullable(),
  hoursWorked: z.number().min(0).max(24).nullable(),
  workPerformed: z.string(),
  issues: z.string().nullable(),
  notes: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  // RDO semantics (migration 0006). Existing pre-signature rows default to
  // status='draft', version=1, signedAt=null.
  status: rdoStatusSchema.default('draft'),
  signedAt: z.string().nullable().default(null),
  signedBy: z.string().nullable().default(null),
  signedByName: z.string().nullable().default(null),
  version: z.number().int().min(1).default(1),
  correctsId: z.string().nullable().default(null),
});
export type DailyLog = z.infer<typeof dailyLogSchema>;

export const createDailyLogRequestSchema = z.object({
  logDate: isoDateSchema,
  weather: weatherSchema.optional(),
  temperatureC: z.number().int().min(-60).max(60).optional(),
  crewSize: z.number().int().min(0).max(1000).optional(),
  hoursWorked: z.number().min(0).max(24).optional(),
  workPerformed: z.string().trim().min(1).max(4000),
  issues: z.string().trim().max(4000).optional(),
  notes: z.string().trim().max(4000).optional(),
});
export type CreateDailyLogRequest = z.infer<typeof createDailyLogRequestSchema>;

export const signRdoRequestSchema = z.object({
  // Client echoes the id it just created so the API can reject a stale
  // "sign" call if the row was already superseded by a correction.
  expectedVersion: z.number().int().min(1).optional(),
});
export type SignRdoRequest = z.infer<typeof signRdoRequestSchema>;

export const correctRdoRequestSchema = createDailyLogRequestSchema.extend({
  // The RDO id being corrected. API verifies it's signed + same project.
  correctsId: z.string().min(1),
  correctionNote: z.string().trim().min(1).max(1000),
});
export type CorrectRdoRequest = z.infer<typeof correctRdoRequestSchema>;
